<template>
  <accordion-panel
    class="chart-legends"
    header-class="chart-legends__header"
    :id="chartId"
    :collapsed.sync="collapsed"
    @update:collapsed="handleAccordionOnPrint"
  >
    <template #title>
      <strong> {{ $t('general.chart-legends') }} </strong>
    </template>
    <b-checkbox
      class="chart-legends__toggle-all"
      :checked="isAllLegendsSelected"
      @change="checkAllLegends"
    />
    <span>
      {{ $t('general.mark-chart-variables') }}
    </span>
    <ul class="chart-legends__list">
      <li
        v-for="(item, index) in orderedItems"
        class="chart-legends__list-item"
        :style="getVisibilityStyle(item)"
        :key="index"
      >
        <div
          :class="{ 'chart-legends__disabled': isLegendHidden(item) }"
        >
          <b-checkbox
            class="chart-legends__checkbox"
            :checked="!isLegendHidden(item)"
            @change="toggleLegend(item)"
          />
          <span
            class="chart-legends__color"
            :style="getColorStyle(item.chartColor)"
          />
          <span
            class="chart-legends__checkbox-label"
            v-html="item[itemsUniqueProperty]">
          </span>
        </div>
      </li>
    </ul>
  </accordion-panel>
</template>

<script>
import { latinize } from '@/services/stringHelper';
import { getColorsListByLength } from '@/domain/data/charts/chartColors';
import { getLineTypesListByLength } from '@/domain/data/charts/chartLineTypes';
import AccordionPanel from '@/components/AccordionPanel';
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/common';

export default {
  name: 'ChartLegends',
  props: {
    items: {
      type: Array,
      required: true,
    },
    hiddenLegends: {
      type: Array,
      default: () => [],
    },
    chartId: {
      type: String,
      required: true,
    },
    itemsUniqueProperty: {
      type: String,
      default: 'name',
    },
    colorsList: {
      type: Array,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    isOscyllography: {
      type: Boolean,
      default: false,
    },
  },
  components: { AccordionPanel },
  data: vm => ({
    collapsed: vm.startCollapsed,
    allLegendsSelected: true,
    estilosLinha: ['5, 5, 2', '10, 10, 5', '15, 5, 5'],
  }),
  computed: {
    orderedItems() {
      const items = this.getItemsWithColors();
      return (items || []).sort((a, b) => {
        if (
          latinize(a[this.itemsUniqueProperty])
          < latinize(b[this.itemsUniqueProperty])
        ) {
          return -1;
        }
        if (
          latinize(a[this.itemsUniqueProperty])
          > latinize(b[this.itemsUniqueProperty])
        ) {
          return 1;
        }
        return 0;
      });
    },
    isAllLegendsSelected() {
      return this.items.every(item => !this.isLegendHidden(item));
    },
    chartColors() {
      return getColorsListByLength(this.items.length, this.colorsList);
    },
    chartLinetypes() {
      return getLineTypesListByLength(this.orderedManobras.length);
    },
  },
  created() {
    this.initListeners();
    this.handleAccordionOnPrint();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    handleAccordionOnPrint() {
      const originalState = this.collapsed;
      window.onbeforeprint = () => {
        this.openAccordion();
      };
      window.onafterprint = () => {
        this.collapsed = originalState;
      };
    },
    openAccordion() {
      this.collapsed = false;
    },
    initListeners() {
      eventBus.on(events.print, this.openAccordion);
    },
    removeListeners() {
      eventBus.off(events.print, this.openAccordion);
    },
    getVisibilityStyle(variable) {
      return {
        display: (variable.values || []).length ? 'block' : 'none',
      };
    },
    getColorStyle(color) {
      return {
        'background-color': color,
      };
    },
    toggleLegend(item) {
      this.$emit('toggle-legend', item);
    },
    isLegendHidden(item) {
      return this.hiddenLegends.includes(item[this.itemsUniqueProperty]);
    },
    checkAllLegends(event) {
      if (event) {
        this.$emit('select-all-legends');
        return;
      }
      this.$emit('unselect-all-legends');
    },
    getItemsWithColors() {
      return this.items.map((item, index) => ({
        ...item,
        chartColor: this.chartColors[index],
      }));
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.chart-legends
  border: 1px solid $color-border-gray
  border-radius: 5px
  ::v-deep &__header
    min-height: 25px
    padding: 0 15px !important
  &__disabled
    opacity: .5
  &__list
    position: relative
    padding-left: 20px
    column-count: 2
    list-style-type: none
    margin-bottom: 0
    +media-max-md
      column-count: 1
  &__checkbox
    display: inline-flex
    vertical-align: middle
  &__list-item
    margin-bottom: 10px
    display: flex
    align-items: center
  &__toggle-all
    margin-bottom: 10px
    margin-left: 20px
    display: inline-block
  &__checkbox-label
    vertical-align: middle
    white-space: normal
    word-break: break-word
  &__color
    display: inline-block
    vertical-align: middle
    margin: 5px 3px 5px 0
    width: 15px
    height: 15px
.preview
    background-color: #ffffff
    max-width: 50px
    border: 1px solid #000
    border-radius: 5px
    padding: 5px
    margin-right: 10px
</style>
